import type { FC } from "react";
import { useRef, useState, useEffect } from "react";
import * as Sentry from "@sentry/react";
import { findAddressByStreetAndHouseNumber } from "../../services/addresses";
import { SearchBox } from "@mapbox/search-js-react";
import {
  getWorkerIdForUser,
  updateWorkerLocation,
} from "../../services/workers";
import { useAuth } from "../../contexts/AuthContext";
import LocationIQSearch from "../../components/LocationIQSearch";

export interface AddressDetails {
  streetNumber: string;
  streetName: string;
  zipcode: string;
  city: string;
  country: string;
  fullAddress: string;
  source?: "search" | "gps";
  existingAddressId?: number | null;
}

interface AddressFormProps {
  addressDetails: AddressDetails;
  onInputChange: (_event: React.ChangeEvent<HTMLInputElement>) => void;
  onSave: () => void;
  saveStatus: string;
  setCurrentLocation: (
    _location: { latitude: number; longitude: number } | null
  ) => void;
  updateAddressDetails?: (_details: AddressDetails) => void;
  currentLocation?: { latitude: number; longitude: number } | null;
  isSaving?: boolean;
}

const AddressForm: FC<AddressFormProps> = ({
  addressDetails: initialAddressDetails,
  onInputChange,
  onSave,
  saveStatus,
  setCurrentLocation,
  currentLocation,
  isSaving = false,
}) => {
  const inputRef = useRef<HTMLDivElement>(null);
  const rateLimitTimeoutRef = useRef<number | null>(null);
  const { user } = useAuth();

  // State to track if we found an existing address
  const [existingAddressFound, setExistingAddressFound] = useState(false);

  // Add state for proximity coordinates
  const [proximityCoordinates, setProximityCoordinates] = useState<
    string | null
  >(null);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [isMapboxRateLimited, setIsMapboxRateLimited] = useState(false);

  // Default coordinates (Berlin)
  const DEFAULT_COORDINATES = "13.405,52.52"; // Format: longitude,latitude

  // Get search provider from environment variables
  const searchProvider = import.meta.env["VITE_SEARCH_PROVIDER"] || "mapbox";
  const mapboxToken = import.meta.env["VITE_MAPBOX_ACCESS_TOKEN"] as string;
  const locationiqToken = import.meta.env[
    "VITE_LOCATIONIQ_ACCESS_TOKEN"
  ] as string;
  const shouldUseLocationIQSearch =
    searchProvider === "locationiq" ||
    (isMapboxRateLimited && Boolean(locationiqToken));
  const activeSearchProvider = shouldUseLocationIQSearch
    ? "locationiq"
    : "mapbox";

  useEffect(() => {
    return () => {
      if (rateLimitTimeoutRef.current) {
        window.clearTimeout(rateLimitTimeoutRef.current);
      }
    };
  }, []);

  // Update proximity when component mounts or currentLocation changes
  useEffect(() => {
    // If we have the user's current location, use it for proximity
    if (currentLocation) {
      setProximityCoordinates(
        `${currentLocation.longitude},${currentLocation.latitude}`
      );
    } else {
      // Otherwise use default coordinates
      setProximityCoordinates(DEFAULT_COORDINATES);
    }
  }, [currentLocation]);

  const getSearchErrorStatus = (error: unknown): number | null => {
    if (!error || typeof error !== "object") return null;
    const record = error as {
      status?: number;
      statusCode?: number;
      response?: { status?: number };
    };
    return (
      record.status ?? record.statusCode ?? record.response?.status ?? null
    );
  };

  const handleSearchError = (error: unknown) => {
    const status = getSearchErrorStatus(error);
    const normalizedError =
      error instanceof Error
        ? error
        : new Error(
            typeof error === "string" ? error : "Address search failed"
          );

    Sentry.captureException(normalizedError, {
      level: "warning",
      tags: {
        operation: "mapbox.search",
        provider: activeSearchProvider,
        route: "/location",
        handled: "true",
        status: status ? String(status) : "unknown",
      },
    });

    if (status === 429) {
      setIsMapboxRateLimited(true);
      setSearchError(
        locationiqToken
          ? "Mapbox ist kurzzeitig ausgelastet. Es wird ein alternativer Suchdienst verwendet."
          : "Zu viele Suchanfragen. Bitte versuchen Sie es gleich erneut."
      );

      if (rateLimitTimeoutRef.current) {
        window.clearTimeout(rateLimitTimeoutRef.current);
      }
      rateLimitTimeoutRef.current = window.setTimeout(() => {
        setIsMapboxRateLimited(false);
        setSearchError(null);
      }, 30_000);
      return;
    }

    setSearchError(
      "Adresse konnte nicht verarbeitet werden. Bitte versuchen Sie es erneut."
    );
  };

  // Unified handler for both search providers
  const handleSearchRetrieve = async (response: any) => {
    try {
      console.log(`${activeSearchProvider} response:`, response);
      Sentry.addBreadcrumb({
        category: "mapbox.search",
        level: "info",
        message: "mapbox.search.retrieve",
        data: {
          provider: activeSearchProvider,
          route: "/location",
          featureCount: response?.features?.length ?? 0,
        },
      });

      const feature = response?.features?.[0];

      if (!feature) {
        throw new Error("Address search response did not include a feature");
      }

      // Extract address components
      const properties = feature.properties ?? {};
      const context = properties.context || {};

      // Get street name and number from context.address
      const addressContext = context.address || {};
      let streetName = addressContext.street_name || "";
      let streetNumber = addressContext.address_number || "";

      // Fallback: If street name and number are not available in context.address,
      // try to extract them from the full address
      if (!streetName || !streetNumber) {
        const fullAddress = feature.place_name || properties.full_address || "";
        const addressParts = fullAddress.split(",")[0].trim(); // Get the part before the first comma

        // Try to match a pattern like "Streetname Number"
        const match = addressParts.match(/^(.*?)\s+(\d+[\w]*)$/);
        if (match) {
          if (!streetName) streetName = match[1].trim();
          if (!streetNumber) streetNumber = match[2].trim();
        }
      }

      // Check if address exists in database
      let foundExistingId: number | null = null;
      setExistingAddressFound(false);
      if (streetName && streetNumber) {
        try {
          const existingAddress = await findAddressByStreetAndHouseNumber(
            streetName,
            streetNumber,
            {
              zipCode: context.postcode?.name || "",
              city: context.place?.name || "",
              country: context.country?.name || "Deutschland",
            }
          );

          if (existingAddress) {
            console.log("Found existing address:", existingAddress.id);
            setExistingAddressFound(true);
            foundExistingId = existingAddress.id;
          }
        } catch (error) {
          console.error("Error checking address existence:", error);
        }
      }

      // Create the updated address details with source tracking
      const updatedAddressDetails = {
        ...initialAddressDetails,
        fullAddress: feature.place_name || properties.full_address || "",
        streetNumber,
        streetName,
        city: context.place?.name || "",
        zipcode: context.postcode?.name || "",
        country: context.country?.name || "Deutschland",
        source: "search" as const,
        existingAddressId: foundExistingId,
      };

      // Update all address details
      onInputChange({
        target: {
          name: "addressDetails",
          value: updatedAddressDetails,
        },
      } as any);

      // Update the current location in LocationPage using the coordinates from the feature
      const coordinates = feature.geometry?.coordinates;
      if (
        !Array.isArray(coordinates) ||
        coordinates.length < 2 ||
        !Number.isFinite(coordinates[0]) ||
        !Number.isFinite(coordinates[1])
      ) {
        throw new Error("Address search response did not include coordinates");
      }

      const [longitude, latitude] = coordinates;
      setCurrentLocation({ latitude, longitude });
      setSearchError(null);
    } catch (error) {
      handleSearchError(error);
    }
  };

  // Enhanced save function that also updates worker location
  const handleSaveWithLocationUpdate = async () => {
    if (isSaving) return; // double-click guard

    try {
      // First call the original onSave function
      await onSave();

      // Then update worker location if we have current location and user
      if (currentLocation && user?.id) {
        try {
          const workerId = await getWorkerIdForUser(user.id);
          await updateWorkerLocation(
            workerId,
            currentLocation.latitude.toString(),
            currentLocation.longitude.toString()
          );
          console.log("Worker location updated successfully");
        } catch (error) {
          console.error("Error updating worker location:", error);
          // Don't show error to user since the main save operation succeeded
        }
      }
    } catch (error) {
      console.error("Error in save operation:", error);
      // The original onSave function will handle error display
    }
  };

  return (
    <div className="rounded-lg bg-white p-4 shadow-sm dark:bg-gray-800">
      <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
        Adressdetails
      </h2>
      <div className="space-y-4">
        <div className="relative">
          <label
            htmlFor="fullAddress"
            className="mb-1 block text-sm font-medium text-gray-900 dark:text-white"
          >
            Adresse
          </label>
          <div ref={inputRef} className="w-full">
            {shouldUseLocationIQSearch ? (
              <LocationIQSearch
                accessToken={locationiqToken}
                options={{
                  language: "de",
                  country: "DE",
                  ...(proximityCoordinates && {
                    proximity: proximityCoordinates,
                  }),
                }}
                onRetrieve={(response) => {
                  void handleSearchRetrieve(response);
                }}
                value={initialAddressDetails.fullAddress}
                placeholder="Geben Sie eine Adresse ein"
              />
            ) : isMapboxRateLimited ? (
              <input
                id="fullAddress"
                type="text"
                value={initialAddressDetails.fullAddress}
                disabled
                className="block w-full rounded-lg border border-gray-300 bg-gray-100 p-2.5 text-sm text-gray-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-400"
                placeholder="Suche kurzzeitig pausiert"
              />
            ) : (
              <SearchBox
                accessToken={mapboxToken}
                options={{
                  language: "de",
                  country: "DE",
                  types: "address",
                  // Add proximity if we have coordinates
                  ...(proximityCoordinates && {
                    proximity: proximityCoordinates,
                  }),
                }}
                onSuggestError={handleSearchError}
                onRetrieve={(response) => {
                  void handleSearchRetrieve(response);
                }}
                value={initialAddressDetails.fullAddress}
                placeholder="Geben Sie eine Adresse ein"
              />
            )}
          </div>
          {searchError && (
            <p className="mt-2 text-sm text-red-600 dark:text-red-500">
              {searchError}
            </p>
          )}
        </div>
      </div>
      <div className="mt-6 flex items-center gap-4">
        <button
          onClick={handleSaveWithLocationUpdate}
          disabled={isSaving}
          className={`rounded-lg px-4 py-2 text-sm font-medium text-white ${
            isSaving
              ? "cursor-not-allowed bg-gray-400"
              : "bg-primary-700 hover:bg-primary-800"
          }`}
        >
          {isSaving ? "Wird gespeichert..." : "Weiter zu Kunden"}
        </button>
        {existingAddressFound && (
          <span className="text-sm text-green-600 dark:text-green-500">
            Bestehende Adresse gefunden!
          </span>
        )}
      </div>
      {saveStatus && (
        <p
          className={`mt-2 text-sm ${
            saveStatus.includes("Fehler")
              ? "text-red-600 dark:text-red-500"
              : "text-green-600 dark:text-green-500"
          }`}
        >
          {saveStatus}
        </p>
      )}
    </div>
  );
};

export default AddressForm;
